import LoadingScreen   from './screens/LoadingScreen.js';
import StartScreen     from './screens/StartScreen.js';
import PauseMenu       from './screens/PauseMenu.js';
import MissionEndedScreen from './screens/MissionEndedScreen.js';
import ControlsOverlay from './overlays/ControlsOverlay.js';
import CreditsOverlay  from './overlays/CreditsOverlay.js';
import SettingsOverlay from './overlays/SettingsOverlay.js';
import { initUISound, playUIClick } from './uiSound.js';
import { initMissionEndedSound } from './missionEndedSound.js';
import { clearAll as clearMissionRecords, getBest } from '../mission/MissionRecords.js';
import { clearAll as clearSettings } from '../core/UserSettings.js';
import { clearIntroSeen } from '../core/FirstRun.js';
import MissionStats from '../mission/MissionStats.js';

/**
 * Top-level UI / view manager.
 *
 * Owns a single root element layered over the 3D viewport and renders exactly
 * one "screen" at a time (loading / start / playing / paused), plus an optional
 * modal "overlay" (settings / credits / controls) on top of the active screen.
 *
 * `App` drives state transitions and registers intent callbacks via on*();
 * the screens emit user intent back through those callbacks.
 *
 *   loading ──Enter──▶ start ──Fly Now──▶ playing ⇄ paused
 *      │                 ▲                    ▲  │
 *      └─Enter (1st run)─┼─▶ intro ───────────┘  └──End Mission──▶ ended
 *                        └────────── Exit / Return ─────────────┘
 *
 * `intro` shows no screen at all: the first-run intro (ui/IntroSequence.js)
 * draws its own overlay, and App hands straight from it into a mission.
 */
export default class UIManager {
  constructor() {
    this._state = 'loading';      // loading | intro | start | playing | paused | ended
    this._overlay = null;         // active overlay key or null
    this._cb = { enter: null, launch: null, resume: null, restart: null, exit: null, endMission: null, lighting: null, armament: null, setting: null };

    this.root = document.createElement('div');
    this.root.id = 'ui-root';
    document.getElementById('app').appendChild(this.root);

    // Full-screen fade-to-black, layered above every screen. Used to hide the
    // aircraft jump when launching a scenario whose spawn differs from the
    // parked start-screen pose. CSS drives the opacity transition.
    this._fade = document.createElement('div');
    this._fade.className = 'fade-overlay';
    this.root.appendChild(this._fade);

    // ── Screens ───────────────────────────────────────────────────────────
    this.loading = new LoadingScreen({ onEnter: () => this._enter() });
    this.start = new StartScreen({
      onLaunch: (cfg) => this._cb.launch?.(cfg),
      onLightingChange: (key) => this._changeLighting(key),
      onArmamentChange: (arm) => this._cb.armament?.(arm),
      onCloudsChange: (coverage) => this._cb.clouds?.(coverage),
      onOpenOverlay: (key) => this.openOverlay(key),
      onNoHudChange: (noHud) => this._changeHud(!noHud),
      onReviewResult: (code) => this._reviewMission(code),
    });
    this.pause = new PauseMenu({
      onResume: () => this._cb.resume?.(),
      onRestart: () => this._cb.restart?.(),
      onEndMission: () => this._cb.endMission?.(),
      onExit: () => this._cb.exit?.(),
      onLightingChange: (key) => this._changeLighting(key),
      onOpenOverlay: (key) => this.openOverlay(key),
    });
    this.missionEnded = new MissionEndedScreen({ onReturn: () => this.returnFromEnded() });
    this._reviewMode = false; // ended sheet opened as a read-only review (from start)
    this.root.append(this.loading.el, this.start.el, this.pause.el, this.missionEnded.el);

    // ── Overlays ──────────────────────────────────────────────────────────
    this.overlays = {
      controls: new ControlsOverlay({ onClose: () => this.closeOverlay() }),
      credits:  new CreditsOverlay({ onClose: () => this.closeOverlay() }),
      settings: new SettingsOverlay({
        onClose: () => this.closeOverlay(),
        onChange: (key, value) => {
          if (key === 'hud') this._changeHud(value);
          else {
            // Off disables the deck, so the start screen's coverage slider has
            // nothing to drive — grey it out alongside applying the setting.
            if (key === 'cloudQuality') this.start.setCloudsEnabled(value !== 'off');
            this._cb.setting?.(key, value);
          }
        },
        // Wipe every saved record: mission data (re-badging the mission list so
        // cleared grades disappear immediately) and the stored settings.
        // Restoring the defaults re-persists them as it applies each one live,
        // so the settings blob has to be dropped after that, not before.
        onClearData: () => {
          clearMissionRecords();
          this.start.refreshMissionGrades();
          this.overlays.settings.resetToDefaults();
          clearSettings();
          // A wipe makes this browser a stranger again: next boot is a first run.
          clearIntroSeen();
        },
      }),
    };
    for (const o of Object.values(this.overlays)) this.root.appendChild(o.el);

    // The saved HUD preference drives both the settings toggle and the start
    // screen's NO HUD checkbox — seed the checkbox from the overlay's restored
    // value. App loaded the same settings itself, so nothing needs applying.
    this.start.setNoHud(!this.overlays.settings.values.hud);
    // Same for the cloud slider against the restored cloud-quality setting.
    this.start.setCloudsEnabled(this.overlays.settings.values.cloudQuality !== 'off');

    initUISound();
    initMissionEndedSound();
    this._wireClickSound();

    this._render();
  }

  // Play a click sample on any interactable under the UI root. Delegated so it
  // covers every screen + overlay (all parented to root) without touching each
  // control. Buttons fire on `click`; checkboxes and the <select> fire on
  // `change` instead — that's the toggle event, and it keeps the click that
  // also toggles a checkbox from double-beeping. Range sliders are intentionally
  // left out (a drag would machine-gun the sound).
  _wireClickSound() {
    this.root.addEventListener('click', (e) => {
      const t = e.target;
      if (t instanceof Element && t.closest('button')) playUIClick();
    });
    this.root.addEventListener('change', (e) => {
      const t = e.target;
      if (t instanceof Element && t.matches('input[type="checkbox"], select')) playUIClick();
    });
  }

  // ── Intent registration (App listens) ───────────────────────────────────
  onEnter(cb)         { this._cb.enter = cb; }
  onLaunch(cb)        { this._cb.launch = cb; }
  onResume(cb)        { this._cb.resume = cb; }
  onRestart(cb)       { this._cb.restart = cb; }
  onEndMission(cb)    { this._cb.endMission = cb; }
  onExitToStart(cb)   { this._cb.exit = cb; }
  onLightingChange(cb){ this._cb.lighting = cb; }
  onArmamentChange(cb){ this._cb.armament = cb; }
  onCloudsChange(cb)  { this._cb.clouds = cb; }
  onSettingChange(cb) { this._cb.setting = cb; }

  get state()       { return this._state; }
  get overlayOpen() { return this._overlay !== null; }
  get selection()   { return this.start.selection; }
  /** Loadout the current mission selection launches with (scenario-pinned or player-chosen). */
  get armament()    { return this.start.armament; }
  /** Preselect a mission on the start screen (by SCENARIOS index). */
  selectScenario(index) { this.start.selectScenario(index); }
  /** A launch config built off the start screen's selection, with overrides. */
  launchConfig(overrides) { return this.start.launchConfig(overrides); }

  /** Lock/unlock the settings overlay's NO HUD toggle (locked in combat). */
  setHudLocked(locked, value) { this.overlays.settings.setHudLocked(locked, value); }

  /** Lock/unlock the pause menu's lighting picker (locked in combat). */
  setLightingLocked(locked) { this.pause.setLightingLocked(locked); }

  // ── Loading ──────────────────────────────────────────────────────────────
  setProgress(loaded, total) { this.loading.setProgress(loaded, total); }
  assetsReady()              { this.loading.ready(); }

  _enter() {
    // Fade to black so the swap from the loading screen to the live start
    // preview (scene build + camera jump) is hidden, then fade back in. App's
    // enter handler returns true when it has started the first-run intro
    // instead, which puts up its own black — so lifting this one shows nothing.
    this.fadeToBlack().then(() => {
      const intro = this._cb.enter?.();
      this._set(intro ? 'intro' : 'start');
      return this.fadeFromBlack();
    });
  }

  // ── Transitions driven by App ────────────────────────────────────────────
  showStart()    { this.start.reset(); this.start.refreshMissionGrades(); this._set('start'); }
  enterPlaying() { this._closeOverlaySilently(); this._set('playing'); }
  showPause(endMissionAvailable = false) {
    this.pause.syncLighting(this.start.selection.lighting);
    this.pause.setEndMissionAvailable(endMissionAvailable);
    this._set('paused');
  }
  /** Show the end-of-mission score sheet built from a MissionStats snapshot. */
  showMissionEnded(stats, outcome) {
    this._reviewMode = false;
    this.missionEnded.render(stats, outcome);
    this._set('ended');
  }

  /**
   * Reopen the score sheet for a mission's best saved run (from the start screen)
   * as a read-only review. Returning from it goes back to the start screen
   * rather than exiting a scenario. No-op if the mission has no saved result.
   */
  _reviewMission(code) {
    const best = getBest(code);
    const stats = best && MissionStats.fromResult(best);
    if (!stats) return;
    this._reviewMode = true;
    this.missionEnded.render(stats, best.outcome);
    this._set('ended');
  }

  /**
   * Leave the score sheet — back to the start screen when reviewing a saved run,
   * otherwise exit the active scenario (the normal end-of-mission path). Bound to
   * the sheet's button and App's R / Enter handler.
   */
  returnFromEnded() {
    if (this._reviewMode) { this._reviewMode = false; this.showStart(); }
    else this._cb.exit?.();
  }

  // ── Overlays ──────────────────────────────────────────────────────────────
  openOverlay(key) {
    if (!this.overlays[key]) return;
    this._overlay = key;
    this._render();
  }
  closeOverlay() {
    this._overlay = null;
    this._render();
  }
  /** Esc pressed: if an overlay is open, close it and report it was consumed. */
  handleEscapeOverlay() {
    if (this._overlay) { this.closeOverlay(); return true; }
    return false;
  }

  /** Route a lighting change so start screen + pause menu stay in sync. */
  _changeLighting(key) {
    this.start.setLighting(key);
    this.pause.syncLighting(key);
    this._cb.lighting?.(key);
  }

  /** Route a HUD-visibility change so the start screen NO HUD checkbox and the
   * settings panel toggle stay in sync (and App applies it live). */
  _changeHud(hud) {
    this.start.setNoHud(!hud);
    this.overlays.settings.setHud(hud);
    this._cb.setting?.('hud', hud);
  }

  // ── Fade transition ───────────────────────────────────────────────────────
  /** Fade the viewport to black. Resolves when fully opaque. */
  fadeToBlack() { return this._fadeTo(true); }
  /** Fade the viewport back from black. Resolves when fully transparent. */
  fadeFromBlack() { return this._fadeTo(false); }

  _fadeTo(opaque) {
    return new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        clearTimeout(fallback);
        this._fade.removeEventListener('transitionend', finish);
        resolve();
      };
      // Fallback in case transitionend never fires (e.g. opacity unchanged).
      const fallback = setTimeout(finish, 1200);
      this._fade.addEventListener('transitionend', finish);
      // Toggle on the next frame so the class change always animates.
      requestAnimationFrame(() => this._fade.classList.toggle('is-opaque', opaque));
    });
  }

  _closeOverlaySilently() { this._overlay = null; }

  _set(state) { this._state = state; this._render(); }

  _render() {
    this.loading.setVisible(this._state === 'loading');
    this.start.setVisible(this._state === 'start');
    this.pause.setVisible(this._state === 'paused');
    this.missionEnded.setVisible(this._state === 'ended');
    for (const [key, o] of Object.entries(this.overlays)) o.setVisible(this._overlay === key);
  }
}
